import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { api } from '@/api/sensors';
import { WaterTestResponse } from '@/types/api';
import { useTank } from '@/contexts/TankContext';
import { CACHE_TIMES } from '@/config/cacheTimes';

interface UseWaterTestDataParams {
  startDate: Date;
  endDate: Date;
  paramName?: string;
  device?: string;
  excludeOutliers?: boolean;
  /** Whether to enable the query (defaults to true, combined with tankId check) */
  enabled?: boolean;
}

export function useWaterTestData({
  startDate,
  endDate,
  paramName,
  device,
  excludeOutliers = true,
  enabled = true,
}: UseWaterTestDataParams): UseQueryResult<WaterTestResponse> {
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;

  return useQuery({
    queryKey: ['water-tests', tankId, startDate, endDate, paramName, device, excludeOutliers],
    queryFn: async () => {
      // Format dates as YYYY-MM-DD (backend expects date type, not datetime)
      const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const response = await api.getWaterTestData({
        tank_id: tankId!,
        start_date: formatDate(startDate) + 'T00:00:00',
        end_date: formatDate(endDate) + 'T00:00:00',
        param_name: paramName,
        device,
        exclude_outliers: excludeOutliers,
      });

      return response;
    },
    enabled: !!tankId && enabled,
    staleTime: CACHE_TIMES.DATA,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}
